import type {AdvisorInstance, AdvisorOptions, EventContext} from './types';
import {EventBus} from './eventBus';

export const DEFAULT_DIALOG_WIDTH = '60%';

/** Keeps host-configured dialog widths predictable and prevents invalid CSS from collapsing the modal. */
export function normalizeDialogWidth(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_DIALOG_WIDTH;
  const width = value.trim();
  const match = /^(\d+(?:\.\d+)?)(%|px|vw|vh|rem|em|ch|vmin|vmax)$/.exec(width);
  if (!match) return DEFAULT_DIALOG_WIDTH;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount <= 0) return DEFAULT_DIALOG_WIDTH;
  if (match[2] === '%' && amount > 100) return DEFAULT_DIALOG_WIDTH;
  return width;
}

export class Controller implements AdvisorInstance {
  readonly bus: EventBus;
  private state: {open: boolean; expanded: boolean; dialogWidth: string};
  private listeners = new Set<() => void>();
  opener: HTMLElement | null = null;
  openerBounds: DOMRect | null = null;
  resetHandler: (() => void) | null = null;
  dispose: (() => void) | null = null;
  destroyed = false;

  constructor(readonly options: AdvisorOptions) {
    this.bus = new EventBus(options);
    this.state = {open: false, expanded: false, dialogWidth: normalizeDialogWidth(options.config?.dialogWidth)};
  }

  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private update(next: Partial<typeof this.state>) {
    this.state = {...this.state, ...next};
    this.listeners.forEach(listener => listener());
  }

  open = () => {
    if (this.destroyed || this.state.open) return;
    this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.openerBounds = this.opener && this.opener !== document.body ? this.opener.getBoundingClientRect() : null;
    this.update({open: true});
    this.bus.emit('onAgentOpen', {}, {source: 'api'});
  };

  hide = (reason: EventContext['reason']) => {
    if (this.destroyed || !this.state.open) return;
    this.update({open: false});
    this.bus.emit('onAgentClose', {reason}, {reason, source: reason === 'api' ? 'api' : 'header'});
  };

  close = () => this.hide('api');

  expand = () => {
    if (this.destroyed || this.state.expanded) return;
    this.update({expanded: true});
    this.bus.emit('onAgentExpand', {}, {source: 'api'});
  };

  restore = () => {
    if (this.destroyed || !this.state.expanded) return;
    this.update({expanded: false});
    this.bus.emit('onAgentRestore', {}, {source: 'api'});
  };

  setDialogWidth = (width?: string) => {
    if (this.destroyed) return;
    this.update({dialogWidth: normalizeDialogWidth(width)});
  };

  resetConversation = () => {
    if (!this.destroyed) this.resetHandler?.();
  };

  destroy = () => {
    if (this.destroyed) return;
    this.destroyed = true;
    this.dispose?.();
    this.listeners.clear();
    this.resetHandler = null;
    this.bus.emit('onAgentDestroyed', {}, {source: 'api'});
    this.bus.active = false;
  };
}
